import React from 'react';
import { ArrowRight, ShoppingBag, Package, PackageOpen } from 'lucide-react';
import EmptyState from '@ajat/design-system/EmptyState';
import { ComponentPage, DemoCard } from '../../shared';

export const meta = {
  component: EmptyState,
  componentName: 'EmptyState',
  title: 'Empty State',
  group: 'Data display',
  importPath: '@ajat/design-system/EmptyState',
  description:
    '"Nothing here yet" — an empty cart, no orders, no saved designs, a shop with no products, no FAQ hits: an outline icon (or a brand\'s own `art`), a title, a line of copy and the way out as DS Buttons. The page size is the centred page state; the drawer size the compact one.',
  props: [
    ['icon', 'elementType | node', '—', 'Outline icon — a lucide component drawn at 64px, or any element'],
    ['art', 'node', '—', "Replaces the icon well with the brand's own illustration (OVER/LL: tossed patches)"],
    ['title', 'node', '—', 'Heading — H2 on a page, H3 in a drawer'],
    ['message', 'node', '—', 'One line of copy'],
    ['actions', 'array', '[]', '[{ label, onClick, href, variant, size, iconRight, iconLeft, className }] — DS Buttons; the first is the primary'],
    ['size', "'page' | 'drawer' | 'inline'", "'page'", 'Centred page state (medium-m rhythm, stretches in a flex column), the compact drawer one, or text only inside a list (no icon, no actions)'],
    ['headingLevel', '1 | 2 | 3 | 4', 'by size', 'Heading level override'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'An empty cart',
      code: `import EmptyState from '@ajat/design-system/EmptyState';

<EmptyState
  icon={ShoppingBag}
  title="Din kurv er tom"
  message="Udforsk vores kollektion og find det perfekte."
  actions={[{ label: 'Udforsk kollektion', onClick: goHome, iconRight: <ArrowRight size={20} /> }]}
/>`,
    },
  ],
};

export default function EmptyStatePage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Page" hint='size="page" — the cart / orders / shop state' flush>
        <EmptyState
          icon={ShoppingBag}
          title="Din kurv er tom"
          message="Det ser ud til, at du ikke har tilføjet noget endnu. Udforsk vores kollektion og find det perfekte."
          actions={[{ label: 'Udforsk kollektion', onClick: () => {}, iconRight: <ArrowRight size={20} /> }]}
        />
      </DemoCard>
      <DemoCard title="Two ways out" hint="a shop with no products" flush>
        <EmptyState
          icon={PackageOpen}
          title="Inga produkter än"
          message="Shoppen öppnar snart. Hör av dig om du har frågor."
          actions={[
            { label: 'Kontakta oss', onClick: () => {}, variant: 'black', iconRight: <ArrowRight size={18} /> },
            { label: 'FAQ', onClick: () => {}, variant: 'outline', iconRight: <ArrowRight size={18} /> },
          ]}
        />
      </DemoCard>
      <DemoCard title="Drawer" hint='size="drawer" — compact, H3'>
        <div style={{ maxWidth: 420 }}>
          <EmptyState size="drawer" icon={Package} title="Inga ordrar än" message="Dina ordrar hamnar här när du har beställt." actions={[{ label: 'Börja designa', onClick: () => {}, variant: 'black' }]} />
        </div>
      </DemoCard>
      <DemoCard title="Inline" hint='size="inline" — text only inside a list (FAQ "no results")'>
        <EmptyState size="inline" title="Ingen resultater" message="Prøv et andet søgeord, eller kontakt os." />
      </DemoCard>
    </ComponentPage>
  );
}
